import {
  LitElement,
  html,
  css,
  classMap,
  ref,
  createRef,
  map,
  range,
  unsafeHTML,
  styles,
  paths,
  utils,
  t,
} from '@hh/global.js'
const { throttle, prefersReducedMotion, fadeInAnimation, setCSSVariable } = utils

class HHProductGallery extends LitElement {
  static properties = {
    _current: {
      state: true,
    },
    _total: {
      state: true,
    },
    pagination: {
      reflect: true,
      type: Boolean,
    },
    arrows: {
      reflect: true,
      type: Boolean,
    },
    intersectionObserver: {
      reflect: true,
      type: Boolean,
      attribute: 'intersection-observer',
    },
    initial: {
      type: Number
    },
    slideCount: {
      type: Number,
      attribute: 'slide-count',
    }
  }

  $slides = createRef()
  $next = createRef()
  $prev = createRef()
  $pagination = createRef()
  $paginationPrev = createRef()
  $paginationNext = createRef()

  constructor() {
    super()

    this._total = 0
    this._current = 1

    this.touchstartX = 0
    this.touchendX = 0
    this.swipeThreshold = 50
    this.paginationHeight = 160
    this.paginationThumbHeight = 60
    this.paginationGap = 10

    this.throttledUpdateUI = throttle(this.updateUI.bind(this), 300)
  }

  connectedCallback() {
    super.connectedCallback()

    this.items = Array.from(this.querySelectorAll('[slot="item"]'))
    this._total = this.items.length
    this.prefersReducedMotion = prefersReducedMotion()

    window.addEventListener('resize', this.handleResize.bind(this))
    window.addEventListener('slide:change', this.handleSlideChange.bind(this))

    this.handleResize()
    setTimeout(() => {
      this.handleResize()
      this.handleIntersectionObserver()
    }, 0)
  }

  handleResize() {
    if (this.$pagination?.value?.clientHeight < this.paginationHeight) {
      this.$paginationNext?.value?.setAttribute('disabled', true)
    }

    const paginationWidth = this.$pagination?.value?.closest('.pagination-container')?.clientWidth

    if (paginationWidth && this.slideCount > 0) {
      const paginateWidth = Math.round((paginationWidth - this.slideCount * 10) / this.slideCount)
      setCSSVariable('--hh-pagination-button-size', `${Math.min(paginateWidth, 80)}px`)
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    window.removeEventListener('slide:change', this.handleSlideChange)
  }

  firstUpdated() {
    this.$slides?.value?.addEventListener('scroll', this.throttledUpdateUI, {
      passive: true,
    })

    setTimeout(() => {
      if (this.initial || this.initial === 0) this.moveTo(this.initial)
    }, 300);

    if (this.intersectionObserver && !this.prefersReducedMotion) {
      this.handleIntersectionObserver()
    }
  }

  handleSlideChange (e) {
    const mediaId = e.detail.id
    const slide = this.items.find(item => item.dataset.mediaId === mediaId)

    if (!slide) return

    this.moveTo(parseInt(slide.getAttribute('slide-index')) - 1)
  }

  updated(changedProperties) {
    if (changedProperties.has('_current')) {
      this.setSlideVisibility()
    }
  }

  updateUI() {
    if (!this._total) return

    this._current = (this.items.findIndex(item => item.classList.contains('active')) || 0) + 1
  }

  setSlideVisibility() {
    this.items.forEach((item, idx) => {
      item.classList.remove('hidden')
      const linkElements = item.querySelectorAll('hh-button')
      if (linkElements.length === 0) return

      const idxOffset = idx + 1
      if (idxOffset === this._current) {
        linkElements.forEach((button) => {
          const linkElement = button.shadowRoot.querySelector('a')
          linkElement?.removeAttribute('tabindex')
        })
        item.setAttribute('aria-hidden', 'false')
        item.removeAttribute('tabindex')
      } else {
        linkElements.forEach((button) => {
          const linkElement = button.shadowRoot.querySelector('a')
          linkElement?.setAttribute('tabindex', '-1')
        })
        item.setAttribute('aria-hidden', 'true')
        item.setAttribute('tabindex', '-1')
      }
    })

    const slideScrollPosition = this.$pagination?.value?.querySelector('.pagination-button[aria-current="true"]')?.offsetTop
    this.$pagination?.value?.scrollTo({
      top: slideScrollPosition - this.paginationThumbHeight,
    })
  }

  rotateSlides() {
    const slideScrollPosition =
      this._current === this._total
        ? 0
        : this.$slides?.value?.scrollLeft + this.items[0].clientWidth
    this.$slides?.value?.scrollTo({
      left: slideScrollPosition,
    })

    setTimeout(() => {
      this.updateUI()
    }, 300);

    if (this._current !== this._total) {
      const nextSlideImage = this.items[this._current].querySelector('img')
      this.eagerLoadImage(nextSlideImage)
    }
  }

  handleIntersectionObserver() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          if (!this.prefersReducedMotion && this.fadein) {
            fadeInAnimation(this.items)
          }
        }
      })
    })
    observer.observe(this.$slides?.value)

    const paginationObserver = new IntersectionObserver(this._onPaginationVisibilityChanged.bind(this), {
      root: this.$pagination?.value,
      rootMargin: `0px ${this.$pagination?.value?.clientWidth}px`,
      threshold: 0.8
    });

    Array.from(this.$pagination?.value?.querySelectorAll('.pagination-button'))
      .forEach(item => paginationObserver.observe(item))
  }

  // Early load image for next slide
  eagerLoadImage(image) {
    if (!image) return
    image.setAttribute('loading', 'eager')
  }

  // Move to next or previous slide
  move(e) {
    e.preventDefault()

    const slideScrollPosition =
      e.currentTarget.name === 'next'
        ? this.$slides?.value?.scrollLeft + this.items[0].clientWidth
        : this.$slides?.value?.scrollLeft - this.items[0].clientWidth

    this.$slides?.value?.scrollTo({
      left: slideScrollPosition,
    })

    setTimeout(() => {
      this.updateUI()
    }, 300);
  }

  // Move to specific slide
  moveTo(slide) {
    this.items.forEach(item => {
      const itemIndex = parseInt(item.getAttribute('slide-index')) - 1
      item.classList[itemIndex === slide ? 'add' : 'remove']('active')
    })

    setTimeout(() => {
      this.updateUI()
    }, 300);

    const nextSlideImage = this.items[slide]?.querySelector('img')
 
    if (nextSlideImage) this.eagerLoadImage(nextSlideImage)
  }

  onPaginationClick(e, slideIndex) {
    e.preventDefault()
    this.moveTo(slideIndex)
  }

  onTouchStart(e) {
    this.touchstartX = e.changedTouches[0].screenX
  }

  onTouchEnd(e) {
    this.touchendX = e.changedTouches[0].screenX
    this.checkSwipe()
  }

  checkSwipe() {
    if (
      this.touchendX < this.touchstartX - this.swipeThreshold ||
      this.touchendX > this.touchstartX + this.swipeThreshold
    ) {
      this.userPause = true
    }
  }

  // Scroll the pagination slide
  paginateMove (e) {
    e.preventDefault()
    const direction = e.currentTarget.name === 'next' ? 1 : -1
    const scrollStep = this.paginationThumbHeight + this.paginationGap

    if (direction === 1) {
      this.$paginationPrev.value.removeAttribute('disabled')
      this.$paginationNext.value.toggleAttribute('disabled', (this.$pagination.value.scrollTop + this.$pagination.value.clientHeight + scrollStep) >= (this.$pagination.value.scrollHeight))
    } else {
      this.$pagination.value.toggleAttribute('disabled', (this.$pagination.value.scrollTop - scrollStep) <= 0)
      this.$paginationNext.value.removeAttribute('disabled')
    }

    this.$pagination.value.scrollBy({ top: scrollStep * direction, behavior: 'smooth' })
  }

  _onPaginationVisibilityChanged (entries) {
    for (const entry of entries) {
      if (entry.isIntersecting) {
        const index = parseInt(entry.target.dataset.index)

        if (index === 0) {
          this.$paginationPrev.value.setAttribute('disabled', true)
        } else {
          this.$paginationPrev.value.removeAttribute('disabled')
        }

        if (index === this._total - 1) {
          this.$paginationNext.value.setAttribute('disabled', true)
        } else {
          this.$paginationNext.value.removeAttribute('disabled')
        }

        break;
      }
    }

    if (this.$pagination?.value?.clientHeight < this.paginationHeight) {
      this.$paginationNext?.value?.setAttribute('disabled', true)
    }
  }

  render() {
    return html`<!-- @ts-ignore no-incompatible-type-binding -->
      <div
        class="${classMap({
          slides: true,
        })}"
        ${ref(this.$slides)}
        @touchstart="${this.onTouchStart}"
        @touchend="${this.onTouchEnd}"
      >
        <slot name="item"></slot>
      </div>
      <div class="controls">
        ${/* Pagination */ ''}
        ${(this.pagination &&
          html`
            <div
              class="${classMap({
                'pagination-container': true,
                hidden: this.slideCount === 1,
              })}"
            >
              <button
                ${ref(this.$paginationPrev)}
                @click="${this.paginateMove}"
                class="pagination-arrow pagination-arrow--prev"
                name="previous"
                aria-label="${t('accessibility.previous_slide')}"
                disabled
              >
                ${unsafeHTML(paths.caret)}
              </button>
              <div
                ${ref(this.$pagination)}
                class="${classMap({
                  pagination: true,
                })}"
              >
                ${
                  this.items.map((item, idx) => {
                    return html`
                      <button
                        class="pagination-button"
                        data-index="${idx}"
                        aria-label="${t(
                          'sections.image_carousel.go_to_slide'
                        )} ${idx + 1}"
                        aria-current="${this._current === idx + 1}"
                        @click="${(e) => this.onPaginationClick(e, idx)}"
                      >
                        <img src="${item.dataset.thumbnailImage}">
                      </button>
                    `
                  })
                }
              </div>
              <button
                ${ref(this.$paginationNext)}
                @click="${this.paginateMove}"
                class="pagination-arrow pagination-arrow--next"
                name="next"
                aria-label="${t('accessibility.next_slide')}"
              >
                ${unsafeHTML(paths.caret)}
              </button>
            </div>
          `) ||
        ''}
        ${/* Previous/next buttons */ ''}
        ${(this.arrows &&
          html`
            <div class="arrows">
              <button
                ${ref(this.$prev)}
                @click="${this.move}"
                class="arrow arrow--prev"
                name="previous"
                aria-label="${t('accessibility.previous_slide')}"
              >
                ${unsafeHTML(paths.caret)}
              </button>
              <button
                ${ref(this.$next)}
                @click="${this.move}"
                class="arrow arrow--next"
                name="next"
                aria-label="${t('accessibility.next_slide')}"
              >
                ${unsafeHTML(paths.caret)}
              </button>
            </div>
          `) ||
        ''}
      </div>`
  }

  static styles = [
    styles.visuallyHidden,
    styles.hidden,
    css`
      *,
      *::before,
      *::after {
        box-sizing: border-box;
      }
      :host {
        display: block;
        position: relative;
      }
      .slides {
        display: grid;
        position: relative;
        width: 100%;
        height: 100%;
        max-height: 100%;
        background-color: var(--hh-carousel-background, transparent);
        overflow-x: auto;
        scroll-snap-type: x mandatory;
        scroll-behavior: var(--hh-carousel-scroll-behavior, smooth);
        scrollbar-width: none;
        -webkit-overflow-scrolling: touch;
      }

      .slides::-webkit-scrollbar {
        display: none;
      }

      .controls {
        position: absolute;
        padding: var(--hh-space-sm) var(--hh-space-xs);
        bottom: 0;
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100%;
        gap: 1rem;
      }

      .pagination-container {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
      }

      .pagination {
        display: flex;
        gap: 1rem;
      }

      .pagination-button {
        width: var(--hh-pagination-button-size, 1.2rem);
        height: 0.3rem;
        background-color: var(--hh-color-subdued);
        border: none;
        cursor: pointer;
        position: relative;
        opacity: 0.2;
        border-radius: var(--hh-button-radius);
      }

      .pagination-button[aria-current="true"] {
        background-color: var(--hh-color-ink);
        opacity: 1;
      }

      :host([full-width]) .pagination-button {
        opacity: 1;
        background-color: var(--hh-color-reverse);
      }

      :host([full-width]) .pagination-button[aria-current="true"] {
        background-color: var(--hh-color-brand-secondary);
      }

      .pagination-arrow {
        display: none;
      }

      .pagination-button img {
        display: none;
      }

      @media screen and (min-width: 990px) {
        .controls {
          width: auto;
          padding: 10px 45px 10px;
        }

        .pagination {
          flex-direction: column;
          max-height: 16.5rem;
          overflow-y: auto;
          padding: 0.2rem 0.6rem 0.2rem 0.3rem;
        }

        .pagination-button {
          width: 5rem;
          height: 6rem;
          min-height: 6rem;
          padding: 0.2rem;
          border: solid 0.2rem transparent;
          opacity: 1;
          background-color: var(--hh-color-background);
        }


        :host([full-width]) .pagination-button[aria-current="true"] {
          background-color: var(--hh-color-background);
        }

        .pagination-button[aria-current="true"] {
          box-shadow: 0 0 0 0.2rem var(--hh-color-brand-tertiary);
          background-color: var(--hh-color-background);
        }

        .pagination-button:focus-visible {
          outline: none;
          border-color: var(--hh-color-focus);
          box-shadow: 0 0 0 0.1rem var(--hh-color-focus);
        }

        .pagination-button img {
          display: block;
          position: absolute;
          left: 0;
          top: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          border-radius: var(--hh-button-radius);
          background-color: var(--hh-color-reverse);
        }

        .pagination-container {
          display: flex;
          align-items: center;
          gap: var(--hh-space-2xs);
          flex-direction: column;
        }

        .pagination-arrow {
          color: var(--hh-color-ink);
          width: var(--hh-arrow-button-size, 1.6rem);
          height: var(--hh-arrow-button-size, 1.6rem);
          padding: 0;
          background-color: transparent;
          border: none;
          cursor: pointer;
          display: flex;
        }

        :host([full-width]) .pagination-arrow {
          color: var(--hh-color-reverse);
        }

        .pagination-arrow:disabled {
          visibility: hidden;
          cursor: default;
        }

        .pagination-arrow--prev {
          transform: rotate(180deg);
        }

        .pagination-arrow--next {
          transform
        }
      }

      :host ::-webkit-scrollbar {
        width: 0.3rem;
      }

      :host ::-webkit-scrollbar-thumb {
        background: var(--hh-color-night);
        border-radius: 0.3rem;
      }

      :host ::-webkit-scrollbar-track {
        background: var(--hh-color-neutral);
        border-radius: 0.3rem;
      }
    `,
  ]
}

customElements.define('hh-product-gallery', HHProductGallery)
